import {
  ArrowLeftRightIcon,
  ChevronDownIcon,
  ExternalLinkIcon,
  FileTextIcon,
  LogOutIcon,
  UnlinkIcon,
} from "@/design/icons";
import type { FC, ReactNode } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/design/ui/dropdown-menu";

import type { ITenderBid } from "../../types";

const initials = (email: string | null): string => {
  if (!email) return "·";
  const name = email.split("@")[0];
  const parts = name.split(/[._-]+/).filter(Boolean);
  return (parts.length >= 2 ? parts[0][0] + parts[1][0] : name.slice(0, 2)).toUpperCase();
};

/** The avatar and its menu: account things only (plus dev-only experiments). */
export const AccountMenu: FC<{
  email: string | null;
  onSignOut: () => void;
  onOpenMytender: () => void;
}> = ({ email, onSignOut, onOpenMytender }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        aria-label="Account"
        className="flex size-[26px] items-center justify-center rounded-full bg-accent-soft-2 text-xs font-bold text-[#a04a00] transition-shadow hover:ring-2 hover:ring-accent-soft-2"
      >
        {initials(email)}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" sideOffset={6} className="w-52">
      {email && <DropdownMenuLabel className="truncate font-normal text-fg-3">{email}</DropdownMenuLabel>}
      <DropdownMenuItem onSelect={onOpenMytender}>
        <ExternalLinkIcon />
        Open mytender
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onSelect={onSignOut} className="text-danger focus:text-danger">
        <LogOutIcon />
        Sign out
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

/** The tender name and its menu: the buried switch/unlink. */
export const TenderMenu: FC<{
  tender: ITenderBid | null;
  bidId: string | null;
  onSwitch: () => void;
  onUnlink: () => void;
  onOpenMytender: () => void;
  onOpenTemplate?: () => void;
}> = ({ tender, bidId, onSwitch, onUnlink, onOpenMytender, onOpenTemplate }) => {
  const title = tender?.bid_title ?? (bidId ? "Linked tender" : "No tender linked");
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Tender menu"
          className="-ml-2 flex w-full min-w-0 max-w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left transition-colors hover:bg-hover"
        >
          <span className={bidId ? "truncate text-base font-semibold" : "truncate text-base font-semibold text-fg-3"}>
            {title}
          </span>
          <ChevronDownIcon className="size-3 shrink-0 text-fg-3" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={4} className="w-60">
        <DropdownMenuLabel className="font-normal">
          <div className="truncate font-semibold">{title}</div>
          <div className="text-xs text-fg-3">{bidId ? "Linked to this document" : "Answers use your library only"}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {bidId && (
          <DropdownMenuItem onSelect={onOpenMytender}>
            <ExternalLinkIcon />
            Open in mytender
          </DropdownMenuItem>
        )}
        {onOpenTemplate && (
          <DropdownMenuItem onSelect={onOpenTemplate}>
            <FileTextIcon />
            Template…
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={onSwitch}>
          <ArrowLeftRightIcon />
          {bidId ? "Switch tender…" : "Link a tender…"}
        </DropdownMenuItem>
        {bidId && (
          <DropdownMenuItem onSelect={onUnlink}>
            <UnlinkIcon />
            Unlink from this document
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/** The workspace header: tender on the left, account on the right. */
export const WorkspaceHeader: FC<{
  tender: ITenderBid | null;
  bidId: string | null;
  email: string | null;
  onSwitch: () => void;
  onUnlink: () => void;
  onOpenMytender: () => void;
  onOpenTemplate?: () => void;
  onSignOut: () => void;
  /** Sits between the tender and the account: the Chat · Tender control. */
  center?: ReactNode;
}> = ({ tender, bidId, email, onSwitch, onUnlink, onOpenMytender, onOpenTemplate, onSignOut, center }) => (
  <header className="flex h-11 shrink-0 items-center gap-2 border-b border-line px-3">
    <div className="min-w-0 flex-1">
      <TenderMenu
        tender={tender}
        bidId={bidId}
        onSwitch={onSwitch}
        onUnlink={onUnlink}
        onOpenMytender={onOpenMytender}
        onOpenTemplate={onOpenTemplate}
      />
    </div>
    {center && <div className="shrink-0">{center}</div>}
    <AccountMenu email={email} onSignOut={onSignOut} onOpenMytender={onOpenMytender} />
  </header>
);
